/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { tasksApi, projectsApi, milestonesApi, adminApi, absencesApi, sprintsApi, taskPlanningApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/ConfigContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import TaskResourcePlanningPanel from '../components/TaskResourcePlanningPanel';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Plus, Target, CalendarBlank, ChartBar, List, Table as TableIcon,
  Flag, Clock, ArrowsHorizontal, Kanban, CaretRight, User, Users, UploadSimple, Trash, PencilSimple, Download, FilePdf, DownloadSimple, AirplaneTilt as CalendarOff, Wrench
} from '@phosphor-icons/react';
import { format, differenceInDays, addDays, startOfDay, eachDayOfInterval, isWithinInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatDate, getLabel, getBadgeClass, getTaskStatusColor, getInitials } from '../lib/utils';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const FALLBACK_TASK_STATUSES = [
  { value: 'todo', label: 'A faire', color: '#64748b' },
  { value: 'analysis', label: 'Analyse', color: '#0284c7' },
  { value: 'development', label: 'Dev', color: '#7c3aed' },
  { value: 'deliverable', label: 'Livrable', color: '#ea580c' },
  { value: 'testing', label: 'Recette', color: '#eab308' },
  { value: 'production', label: 'Prod', color: '#16a34a' },
  { value: 'closed', label: 'Clos', color: '#059669' },
];

const safeJsDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Lun' }, { value: 1, label: 'Mar' }, { value: 2, label: 'Mer' },
  { value: 3, label: 'Jeu' }, { value: 4, label: 'Ven' }, { value: 5, label: 'Sam' }, { value: 6, label: 'Dim' },
];

const countTaskWorkingDates = (startDate, endDate, weekdays) => {
  if (!startDate || !endDate || endDate < startDate || !weekdays?.length) return 0;
  try {
    return eachDayOfInterval({ start: startOfDay(startDate), end: startOfDay(endDate) }).filter(d => weekdays.includes(d.getDay() === 0 ? 6 : d.getDay() - 1)).length;
  } catch { return 0; }
};

function WorkingDaysSelector({ value = [], onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {WEEKDAY_OPTIONS.map(day => (
        <label key={day.value} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer text-xs font-medium transition-colors ${value.includes(day.value) ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-500'}`}>
          <Checkbox checked={value.includes(day.value)} onCheckedChange={(checked) => { if (!checked && value.length === 1) { toast.warning('Selectionnez au moins un jour de travail'); return; } onChange(checked ? [...value, day.value].filter((v,i,a)=>a.indexOf(v)===i).sort() : value.filter(v => v !== day.value)); }} />
          {day.label}
        </label>
      ))}
    </div>
  );
}

const TASK_WORKFLOW = { todo:['analysis','request'], analysis:['request','deliverable','development'], request:['analysis','deliverable','development'], deliverable:['development','testing'], development:['testing'], testing:['development','production'], production:['testing','closed'], closed:[] };
const allowedTaskStatuses = (task, statuses) => { const allowed=new Set([task.status,...(TASK_WORKFLOW[task.status]||[])]); return statuses.filter(s=>allowed.has(s.value)); };

function MultiUserSelect({ users, selectedIds, onChange, placeholder = "Assigner..." }) {
  const selected = users.filter(u => selectedIds.includes(u.id));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start text-left font-normal h-auto min-h-[36px] py-1.5" data-testid="multi-user-select">
          {selected.length === 0 ? (
            <span className="text-muted-foreground text-sm">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {selected.map(u => (
                <span key={u.id} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                  {String(u.name || u.email || 'Utilisateur').split(' ')[0]}
                </span>
              ))}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {users.map(u => (
            <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
              <Checkbox
                checked={selectedIds.includes(u.id)}
                onCheckedChange={(checked) => {
                  if (checked) onChange([...selectedIds, u.id]);
                  else onChange(selectedIds.filter(id => id !== u.id));
                }}
              />
              <Avatar className="w-5 h-5">
                <AvatarFallback className="text-[8px] bg-blue-100 text-blue-700">{getInitials(u.name || u.email || 'U')}</AvatarFallback>
              </Avatar>
              <span className="text-sm">{u.name || u.email || 'Utilisateur'}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MultiProjectSelect({ projects, selectedIds, onChange }) {
  const all = selectedIds.length === 0;
  const label = all ? 'Tous les projets' : selectedIds.length === 1 ? (()=>{const p=projects.find(x=>x.id===selectedIds[0]);return p?`${p.code || ''}${p.code?' - ':''}${p.name || ''}`:'1 projet';})() : `${selectedIds.length} projets selectionnes`;
  return (
    <Popover>
      <PopoverTrigger asChild><Button variant="outline" className="w-64 justify-between font-normal"><span className="truncate">{label}</span><CaretRight size={12}/></Button></PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start">
        <div className="flex items-center justify-between border-b pb-2 mb-2"><b className="text-xs">Selection des projets</b><Button variant="ghost" size="sm" className="h-6 text-xs" onClick={()=>onChange([])}>Tous</Button></div>
        <div className="max-h-64 overflow-y-auto space-y-1">{projects.map(p=><label key={p.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded cursor-pointer"><Checkbox checked={selectedIds.includes(p.id)} onCheckedChange={(v)=>onChange(v?[...selectedIds,p.id]:selectedIds.filter(x=>x!==p.id))}/><span className="text-xs"><b>{p.code}</b> - {p.name}</span></label>)}</div>
      </PopoverContent>
    </Popover>
  );
}

export default function TasksPage() {
  const { getList } = useAppConfig();
  const TASK_STATUSES = getList('task_status', FALLBACK_TASK_STATUSES);
  const { user, isManager, hasPermission } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [users, setUsers] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('all');
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [viewMode, setViewMode] = useState('kanban');
  const [ganttView, setGanttView] = useState('task'); // task | project | resource
  const [kanbanScope, setKanbanScope] = useState('all'); // all | my | team
  const [kanbanRootTaskId, setKanbanRootTaskId] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [draggingTaskId, setDraggingTaskId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importProjectId, setImportProjectId] = useState('from-file');
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importingCSV, setImportingCSV] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  const [newTask, setNewTask] = useState({
    project_id: '', title: '', description: '',
    start_date: new Date(), end_date: addDays(new Date(), 7),
    estimated_days: '', working_weekdays: [0,1,2,3,4], status: 'todo', priority: 'medium', priority_level: 2, position: 1,
    assigned_to: [], milestone_id: '', sprint_id: '', parent_task_id: '', dependencies: [], client_visible: false, self_assignment_enabled: false,
  });

  // Drag state for GANTT
  const dragRef = useRef(null);
  const [dragInfo, setDragInfo] = useState(null);

  useEffect(() => { fetchData(); }, [selectedProjectIds, statusFilter]);

  const fetchData = async () => {
    try {
      const [tasksRes, projRes, usersRes, absRes, sprintRes] = await Promise.all([
        tasksApi.getAll({
          status: statusFilter !== 'all' ? statusFilter : undefined,
        }),
        projectsApi.getAll(),
        adminApi.getVisibleUsers().catch(() => ({ data: [] })),
        absencesApi.getAll().catch(() => ({ data: [] })),
        sprintsApi.getAll().catch(() => ({ data: [] })),
      ]);
      const taskRows = Array.isArray(tasksRes.data) ? tasksRes.data.filter(Boolean) : [];
      const projectRows = Array.isArray(projRes.data) ? projRes.data.filter(Boolean) : [];
      const userRows = Array.isArray(usersRes.data) ? usersRes.data.filter(Boolean) : [];
      const absenceRows = Array.isArray(absRes.data) ? absRes.data.filter(Boolean) : [];
      const sprintRows = Array.isArray(sprintRes.data) ? sprintRes.data.filter(Boolean) : [];
      const selected = (selectedProjectIds.length ? taskRows.filter(t => selectedProjectIds.includes(t.project_id)) : taskRows).sort((a,b)=>(Number(a.priority_level??99)-Number(b.priority_level??99))||(Number(a.position??999)-Number(b.position??999))||String(a.start_date||'').localeCompare(String(b.start_date||'')));
      setTasks(selected);
      const singleProject = selectedProjectIds.length === 1 ? selectedProjectIds[0] : 'all';
      setProjectFilter(singleProject);
      setProjects(projectRows);
      setUsers(userRows);
      setAbsences(absenceRows);
      setSprints(sprintRows);
      
      if (selectedProjectIds.length === 1) {
        const msRes = await milestonesApi.getAll({ project_id: selectedProjectIds[0] });
        setMilestones(Array.isArray(msRes.data) ? msRes.data.filter(Boolean) : []);
      } else {
        setMilestones([]);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    try {
      const data = {
        ...newTask,
        start_date: newTask.start_date.toISOString(),
        end_date: newTask.end_date.toISOString(),
        estimated_days: newTask.estimated_days ? parseFloat(newTask.estimated_days) : null,
        priority_level: parseInt(newTask.priority_level ?? 2),
        position: parseInt(newTask.position ?? 1),
        parent_task_id: newTask.parent_task_id || null,
        assigned_to: newTask.assigned_to.length > 0 ? newTask.assigned_to : [],
        milestone_id: newTask.milestone_id || null,
        sprint_id: newTask.sprint_id || null,
        dependencies: newTask.dependencies || [],
      };
      await tasksApi.create(data);
      toast.success('Tache creee');
      setIsCreateDialogOpen(false);
      setNewTask({
        project_id: '', title: '', description: '',
        start_date: new Date(), end_date: addDays(new Date(), 7),
        estimated_days: '', working_weekdays: [0,1,2,3,4], status: 'todo', priority: 'medium', priority_level: 2, position: 1,
        assigned_to: [], milestone_id: '', sprint_id: '', parent_task_id: '', dependencies: [], client_visible: false, self_assignment_enabled: false,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await tasksApi.update(taskId, { status: newStatus });
      toast.success('Statut mis a jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSelfAssign = async (taskId) => {
    try {
      await tasksApi.selfAssign(taskId);
      toast.success('Vous êtes maintenant affecté à cette tâche');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de vous affecter à cette tâche');
    }
  };

  const handleImportCSV = async (dryRun = false) => {
    if (!importFile) return;
    setImportingCSV(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', importFile);
      if (importProjectId && importProjectId !== 'from-file') formData.append('project_id', importProjectId);
      formData.append('dry_run', dryRun ? 'true' : 'false');
      const isExcel = importFile.name.toLowerCase().endsWith('.xlsx') || importFile.name.toLowerCase().endsWith('.xls');
      if (!isExcel && importProjectId === 'from-file') throw new Error('Le mode multi-projets utilise le fichier Excel V4. Pour un CSV, selectionnez un projet.');
      if (!isExcel) {
        const res = await tasksApi.importCSV(importProjectId, importFile);
        setImportResult(res.data);
        toast.success(`${res.data.created || 0} tache(s) importee(s)`);
      } else {
        const res = await fetch(`${API}/api/tasks/import-excel`, { method: 'POST', body: formData, credentials: 'include' });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Erreur import');
        setImportResult(data);
        if (dryRun) toast.success(`Controle termine : ${data.created || 0} creation(s), ${data.updated || 0} mise(s) a jour`);
        else toast.success(`${data.created || 0} creation(s), ${data.updated || 0} mise(s) a jour`);
      }
      if (!dryRun) fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Erreur import');
    } finally {
      setImportingCSV(false);
    }
  };

  const downloadImportReport = () => {
    if (!importResult) return;
    // REGLE DE GESTION V4.1 : le rapport d'import reprend chaque ligne et son action
    // afin de conserver une preuve simple des creations, mises a jour et erreurs.
    const quote = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const header = ['Ligne','Action','Code tache','Projet','Titre','Message'];
    const rows = (importResult.rows || []).map(r => [r.row,r.action,r.code,r.project,r.title,r.message || '']);
    (importResult.errors || []).forEach((message, i) => {
      if (!rows.some(r => String(r[5] || '').includes(String(message).replace(/^Ligne\s+\d+\s*:\s*/i,'')))) rows.push(['', 'ERREUR', '', '', '', message]);
    });
    const content = '\ufeff' + [header, ...rows].map(row => row.map(quote).join(';')).join('\r\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `rapport_import_taches_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const params = projectFilter !== 'all' ? `?project_id=${projectFilter}` : '';
      const res = await fetch(`${API}/api/tasks/export-excel${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Erreur export');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'taches_export.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel telecharge');
    } catch (e) {
      toast.error('Erreur export Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTaskTemplate = async () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    try {
      const res = await fetch(`${API}/api/tasks/import-template`, { credentials: 'include' });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `Erreur ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_import_taches.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Template download error:', err);
      toast.error(err.message || 'Erreur telechargement modele');
    }
  };

  // Filter tasks based on kanban scope AND resource filter (MUST be before ganttData)
  const filteredByResource = useMemo(() => {
    if (resourceFilter === 'all') return tasks;
    return tasks.filter(t => {
      const ids = Array.isArray(t.assigned_to) ? t.assigned_to : (t.assigned_to ? [t.assigned_to] : []);
      return ids.includes(resourceFilter);
    });
  }, [tasks, resourceFilter]);

  const hierarchyOrder = useCallback((list) => {
    const ids = new Set(list.map(t => t.id));
    const ordered = [];
    const sortTask = (a,b) => (Number(a.priority_level ?? 99)-Number(b.priority_level ?? 99)) || (Number(a.position ?? 999)-Number(b.position ?? 999)) || String(a.start_date||'').localeCompare(String(b.start_date||''));
    const top = list.filter(t => !t.parent_task_id || !ids.has(t.parent_task_id)).sort(sortTask);
    top.forEach(parent => {
      ordered.push(parent);
      list.filter(t => t.parent_task_id === parent.id).sort(sortTask).forEach(child => ordered.push(child));
    });
    list.forEach(t => { if (!ordered.some(x => x.id === t.id)) ordered.push(t); });
    return ordered;
  }, []);

  const ganttTasksOrdered = useMemo(() => hierarchyOrder(filteredByResource), [filteredByResource, hierarchyOrder]);

  // GANTT calculations
  const ganttData = useMemo(() => {
    const ganttTasks = ganttTasksOrdered;
    if (ganttTasks.length === 0) return { days: [], tasks: [], milestones: [], totalDays: 1, startDate: new Date() };
    
    const allDates = ganttTasks.flatMap(t => [safeJsDate(t.start_date), safeJsDate(t.end_date)]).filter(Boolean);
    if (milestones.length > 0) {
      allDates.push(...milestones.map(m => safeJsDate(m.target_date)).filter(Boolean));
    }
    if (!allDates.length) return { days: [], tasks: ganttTasks, milestones: [], totalDays: 1, startDate: startOfDay(new Date()) };
    const minDate = startOfDay(new Date(Math.min(...allDates.map(d=>d.getTime()))));
    const maxDate = startOfDay(new Date(Math.max(...allDates.map(d=>d.getTime()))));
    const totalDays = Math.max(1, differenceInDays(maxDate, minDate) + 14);
    
    const startDate = addDays(minDate, -3);
    const days = eachDayOfInterval({ start: startDate, end: addDays(startDate, totalDays) });
    
    return { days, startDate, totalDays, minDate, maxDate };
  }, [ganttTasksOrdered, milestones]);

  const getTaskPosition = useCallback((task) => {
    const rawStart = safeJsDate(task?.start_date);
    const rawEnd = safeJsDate(task?.end_date);
    if (!rawStart || !rawEnd || !ganttData?.startDate) return { left:'0%', width:'0%', leftDays:0, widthDays:0 };
    const taskStart = startOfDay(rawStart);
    const taskEnd = startOfDay(rawEnd);
    const left = differenceInDays(taskStart, ganttData.startDate);
    const width = Math.max(1, differenceInDays(taskEnd, taskStart) + 1);
    return { left: `${(left / Math.max(1,ganttData.totalDays)) * 100}%`, width: `${(width / Math.max(1,ganttData.totalDays)) * 100}%`, leftDays: left, widthDays: width };
  }, [ganttData]);

  const getMilestonePosition = (milestone) => {
    const raw = safeJsDate(milestone?.target_date);
    if (!raw || !ganttData?.startDate) return {left:'0%'};
    const date = startOfDay(raw);
    const left = differenceInDays(date, ganttData.startDate);
    return { left: `${(left / Math.max(1,ganttData.totalDays)) * 100}%` };
  };

  // Drag handlers for GANTT bars
  const handleGanttMouseDown = useCallback((e, task) => {
    e.preventDefault();
    e.stopPropagation();
    const ganttContainer = e.target.closest('.gantt-timeline');
    if (!ganttContainer) return;
    const containerWidth = ganttContainer.getBoundingClientRect().width;
    const pixelsPerDay = containerWidth / ganttData.totalDays;
    
    const taskData = { id: task.id, start_date: task.start_date, end_date: task.end_date };
    dragRef.current = { taskId: task.id, startX: e.clientX, pixelsPerDay, hasMoved: false };
    setDragInfo({ taskId: task.id, daysDelta: 0 });
    
    const onMouseMove = (me) => {
      if (!dragRef.current) return;
      const dx = me.clientX - dragRef.current.startX;
      if (Math.abs(dx) > 3) dragRef.current.hasMoved = true;
      const daysDelta = Math.round(dx / dragRef.current.pixelsPerDay);
      setDragInfo({ taskId: dragRef.current.taskId, daysDelta });
    };
    
    const onMouseUp = async (me) => {
      if (dragRef.current) {
        const hasMoved = dragRef.current.hasMoved;
        const dx = me.clientX - dragRef.current.startX;
        const daysDelta = Math.round(dx / dragRef.current.pixelsPerDay);
        dragRef.current = null;
        setDragInfo(null);
        if (hasMoved && daysDelta !== 0) {
          try {
            const newStart = addDays(new Date(taskData.start_date), daysDelta);
            const newEnd = addDays(new Date(taskData.end_date), daysDelta);
            await tasksApi.update(taskData.id, {
              start_date: newStart.toISOString(),
              end_date: newEnd.toISOString(),
            });
            toast.success('Dates mises a jour');
            fetchData();
          } catch {
            toast.error('Erreur de mise a jour');
          }
        } else if (!hasMoved) {
          setSelectedTask(task);
        }
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [ganttData]);

  // Stats
  const stats = useMemo(() => {
    const byStatus = TASK_STATUSES.reduce((acc, s) => {
      acc[s.value] = tasks.filter(t => t.status === s.value).length;
      return acc;
    }, {});
    return { total: tasks.length, byStatus };
  }, [tasks]);

  const getAssignees = useCallback((task) => {
    const ids = Array.isArray(task.assigned_to) ? task.assigned_to : (task.assigned_to ? [task.assigned_to] : []);
    return users.filter(u => ids.includes(u.id));
  }, [users]);

  // Gantt row renderer
  const renderGanttRow = useCallback((task, hideAssigneeName = false) => {
    const pos = getTaskPosition(task);
    const assignees = getAssignees(task);
    const isDragging = dragInfo?.taskId === task.id;
    const dragOffset = isDragging ? `${(dragInfo.daysDelta / ganttData.totalDays) * 100}%` : '0%';
    return (
      <div key={task.id} className="flex border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`gantt-task-${task.id}`}>
        <div className="w-72 flex-shrink-0 px-4 py-3 border-r border-slate-200">
          <div className="flex items-center gap-2">
            {!hideAssigneeName && (
              <div className="flex -space-x-1.5">
                {assignees.slice(0, 3).map(u => (
                  <Avatar key={u.id} className="w-6 h-6 border-2 border-white">
                    <AvatarFallback className="text-[8px] bg-blue-100 text-blue-700">{getInitials(u.name || u.email || 'U')}</AvatarFallback>
                  </Avatar>
                ))}
                {assignees.length > 3 && (
                  <span className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[8px] font-bold border-2 border-white">+{assignees.length - 3}</span>
                )}
              </div>
            )}
            <div className={`flex-1 min-w-0 ${task.parent_task_id ? 'pl-3 border-l-2 border-slate-200' : ''}`}>
              <p className="font-medium text-sm text-slate-900 truncate">{task.parent_task_id && <span className="text-slate-400 mr-1">↳</span>}<span className="text-blue-600 mr-1">P{task.priority_level ?? 2}.{task.position ?? 1}</span>{task.title}</p>
              <p className="text-xs text-slate-500">{task.parent_task_id ? 'Sous-tâche · ' : 'Tâche · '}{formatDate(task.start_date)} - {formatDate(task.end_date)} · Charge {Number(task.estimated_days || 0).toFixed(2)} J · Planifiée {Number(task.scheduled_days || 0).toFixed(2)} J{task.sprint_id ? ` · ${sprints.find(sp=>sp.id===task.sprint_id)?.name || 'Sprint'}` : ''}</p>
            </div>
          </div>
        </div>
        <div className="flex-1 relative py-2 gantt-timeline" style={{ cursor: isDragging ? 'grabbing' : 'default' }}>
          <div
            className={`gantt-bar status-${task.status} ${isDragging ? 'opacity-70 ring-2 ring-blue-400' : ''}`}
            style={{ left: `calc(${pos.left} + ${dragOffset})`, width: pos.width, cursor: 'grab' }}
            onMouseDown={(e) => handleGanttMouseDown(e, task)}
            onClick={() => !dragInfo && setSelectedTask(task)}
            title="Glissez pour deplacer"
          >
            <ArrowsHorizontal size={10} className="mr-0.5 opacity-60 flex-shrink-0" />
            <span className="truncate text-xs">{task.parent_task_id ? '↳ ' : ''}{task.title} · {task.progress || 0}%</span>
          </div>
        </div>
      </div>
    );
  }, [ganttData, dragInfo, getTaskPosition, getAssignees, handleGanttMouseDown]);

  const kanbanTasks = useMemo(() => {
    let base = filteredByResource;
    if (kanbanScope === 'my' && user?.id) {
      base = base.filter(t => {
        const ids = Array.isArray(t.assigned_to) ? t.assigned_to : (t.assigned_to ? [t.assigned_to] : []);
        return ids.includes(user.id);
      });
    }
    if (kanbanRootTaskId) {
      base = base.filter(t => t.id === kanbanRootTaskId || t.parent_task_id === kanbanRootTaskId);
    }
    return base;
  }, [filteredByResource, kanbanScope, user, kanbanRootTaskId]);

  const getParentTask = useCallback((task) => task?.parent_task_id ? tasks.find(t => t.id === task.parent_task_id) : null, [tasks]);

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-heading font-extrabold text-gradient">Taches</h1>
          <p className="text-slate-600 mt-1">Gerez les taches et suivez l'avancement</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadTaskTemplate} data-testid="download-task-template-btn" title="Modele Excel">
            <DownloadSimple size={16} className="mr-1" />Modele
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={exporting} data-testid="export-excel-btn" title="Exporter en Excel">
            <Download size={16} className="mr-1" />{exporting ? 'Export...' : 'Exporter'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setIsImportDialogOpen(true); setImportResult(null); }} data-testid="import-csv-btn">
            <UploadSimple size={16} className="mr-1" />Importer
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            {hasPermission('tasks:create') && <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-task-btn">
                <Plus size={18} className="mr-2" />Nouvelle tache
              </Button>
            </DialogTrigger>}
          <DialogContent className="dialog-wide max-w-6xl h-[88vh] max-h-[88vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Créer une tâche</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 flex flex-col mt-2">
              <div className="grid lg:grid-cols-[1.35fr_.55fr_.45fr] gap-3 mb-4">
                <div><Label className="text-xs">Titre *</Label><Input value={newTask.title} onChange={(e)=>setNewTask(p=>({...p,title:e.target.value}))} placeholder="Titre de la tâche" className="h-10 font-medium" data-testid="task-title-input"/></div>
                <div><Label className="text-xs">Statut</Label><Select value={newTask.status} onValueChange={(v)=>setNewTask(p=>({...p,status:v}))}><SelectTrigger className="h-10"><SelectValue/></SelectTrigger><SelectContent>{TASK_STATUSES.map(x=><SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Priorité</Label><Input type="number" min="0" step="1" value={newTask.priority_level} onChange={e=>setNewTask(p=>({...p,priority_level:e.target.value}))} className="h-10"/></div>
              </div>

              <Tabs defaultValue="general" className="flex-1 min-h-0 flex flex-col">
                <TabsList className="w-full justify-start bg-transparent border-b rounded-none h-auto p-0">
                  <TabsTrigger value="general" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Général</TabsTrigger>
                  <TabsTrigger value="delay" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Délais & charge</TabsTrigger>
                  <TabsTrigger value="resources" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Ressources</TabsTrigger>
                  <TabsTrigger value="deps" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Dépendances</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
                  <div className="grid md:grid-cols-[.8fr_1.2fr] gap-3">
                    <div className="rounded-xl border bg-white p-4 space-y-3">
                      <div><Label>Projet *</Label><Select value={newTask.project_id} onValueChange={(v)=>{const project=projects.find(x=>x.id===v);setNewTask(p=>({...p,project_id:v,parent_task_id:'',dependencies:[],start_date:project?.start_date?new Date(project.start_date):p.start_date,end_date:project?.end_date?new Date(project.end_date):p.end_date}));}}><SelectTrigger data-testid="task-project-select"><SelectValue placeholder="Sélectionner"/></SelectTrigger><SelectContent>{projects.map(pr=><SelectItem key={pr.id} value={pr.id}>{pr.code} - {pr.name}</SelectItem>)}</SelectContent></Select></div>
                      <div><Label>Position</Label><Input type="number" min="1" step="1" value={newTask.position} onChange={e=>setNewTask(p=>({...p,position:e.target.value}))} className="max-w-28"/></div>
                    </div>
                    <div className="rounded-xl border bg-white p-4"><Label>Description</Label><Textarea value={newTask.description} onChange={e=>setNewTask(p=>({...p,description:e.target.value}))} rows={5} placeholder="Description de la tâche..."/></div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="rounded-xl border bg-slate-50 p-3 flex items-center justify-between gap-3"><div><b className="text-sm">Visible par le client</b><p className="text-[11px] text-slate-500">Afficher cette tâche dans le portail client interne.</p></div><Checkbox checked={!!newTask.client_visible} onCheckedChange={v=>setNewTask(p=>({...p,client_visible:!!v}))}/></div>
                    <div className="rounded-xl border bg-blue-50/60 p-3 flex items-center justify-between gap-3"><div><b className="text-sm">Auto-affectation</b><p className="text-[11px] text-slate-500">Autoriser l'équipe à se positionner.</p></div><Checkbox checked={!!newTask.self_assignment_enabled} onCheckedChange={v=>setNewTask(p=>({...p,self_assignment_enabled:!!v}))}/></div>
                  </div>
                </TabsContent>

                <TabsContent value="delay" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
                  <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3 text-xs text-blue-800"><b>Les dates définissent le délai.</b> La charge reste indépendante et est exprimée en J/JH.</div>
                  <div className="grid lg:grid-cols-[1.2fr_.6fr] gap-3">
                    <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-4"><CalendarBlank size={19} className="text-blue-600"/><div><b>Fenêtre de réalisation</b><p className="text-[11px] text-slate-500">Période autorisée pour réaliser la tâche.</p></div></div><div className="grid grid-cols-2 gap-3"><div><Label>Date début</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarBlank size={15} className="mr-2"/>{format(newTask.start_date,'dd/MM/yyyy',{locale:fr})}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={newTask.start_date} onSelect={d=>d&&setNewTask(p=>({...p,start_date:d}))} locale={fr}/></PopoverContent></Popover></div><div><Label>Date fin</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarBlank size={15} className="mr-2"/>{format(newTask.end_date,'dd/MM/yyyy',{locale:fr})}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={newTask.end_date} onSelect={d=>d&&setNewTask(p=>({...p,end_date:d}))} locale={fr}/></PopoverContent></Popover></div></div></div>
                    <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-4"><Clock size={19} className="text-indigo-600"/><div><b>Charge de la tâche</b><p className="text-[11px] text-slate-500">Unité métier : J/JH.</p></div></div><Label>Charge prévue (J)</Label><Input type="number" min="0" step="0.01" value={newTask.estimated_days} onChange={e=>setNewTask(p=>({...p,estimated_days:e.target.value}))} placeholder="Ex : 5,00" className="max-w-40 text-lg font-semibold"/></div>
                  </div>
                  <div className="rounded-xl border bg-white p-4"><Label>Jours autorisés pour le lissage</Label><div className="mt-2"><WorkingDaysSelector value={newTask.working_weekdays} onChange={v=>setNewTask(p=>({...p,working_weekdays:v}))}/></div></div>
                </TabsContent>

                <TabsContent value="resources" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
                  <div className="rounded-xl border bg-white p-4"><div className="mb-3"><b>Affectation des ressources</b><p className="text-[11px] text-slate-500">Sélectionnez les ressources. Après création, la répartition détaillée en J sera disponible dans la même fiche.</p></div><MultiUserSelect users={users} selectedIds={newTask.assigned_to} onChange={ids=>setNewTask(p=>({...p,assigned_to:ids}))} placeholder="Affecter une ou plusieurs ressources..."/></div>
                  <div className="rounded-xl border bg-slate-50 p-4 text-sm text-slate-600"><b>Répartition simple</b><p className="text-xs mt-1">La charge totale reste celle de la tâche. Chaque ressource pourra ensuite recevoir sa part en J et ses propres jours de lissage dans le délai.</p></div>
                </TabsContent>

                <TabsContent value="deps" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
                  <div className="grid md:grid-cols-2 gap-3"><div className="rounded-xl border bg-white p-4"><Label>Sous-tâche de</Label><Select value={newTask.parent_task_id||'none'} onValueChange={v=>{if(v==='none')return setNewTask(p=>({...p,parent_task_id:''}));const parent=tasks.find(t=>t.id===v);if(!parent)return;const siblings=tasks.filter(t=>t.parent_task_id===v&&Number(t.priority_level??2)===Number(newTask.priority_level??2));const nextPos=Math.max(0,...siblings.map(t=>Number(t.position||0)))+1;setNewTask(p=>({...p,parent_task_id:v,project_id:parent.project_id,start_date:new Date(parent.start_date),end_date:new Date(parent.end_date),position:nextPos,assigned_to:(p.assigned_to?.length?p.assigned_to:(parent.assigned_to||[])),sprint_id:p.sprint_id||parent.sprint_id||'',working_weekdays:(parent.working_weekdays?.length?parent.working_weekdays:p.working_weekdays)}));}}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="none">Aucune (tâche principale)</SelectItem>{tasks.filter(t=>!t.parent_task_id&&(!newTask.project_id||t.project_id===newTask.project_id)).map(t=><SelectItem key={t.id} value={t.id}>P{t.priority_level??2}.{t.position??1} - {t.title}</SelectItem>)}</SelectContent></Select></div><div className="rounded-xl border bg-white p-4"><Label>Sprint</Label><Select value={newTask.sprint_id||'none'} onValueChange={v=>setNewTask(p=>({...p,sprint_id:v==='none'?'':v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="none">Aucun sprint</SelectItem>{sprints.filter(sp=>!newTask.project_id||sp.project_id===newTask.project_id).map(sp=><SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}</SelectContent></Select></div></div>
                  <div className="rounded-xl border bg-white p-4"><Label>Dépend de</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start font-normal">{newTask.dependencies.length?`${newTask.dependencies.length} dépendance(s)`:'Aucune dépendance'}</Button></PopoverTrigger><PopoverContent className="w-80 p-2"><div className="max-h-52 overflow-y-auto">{tasks.filter(t=>t.project_id===newTask.project_id).map(t=><label key={t.id} className="flex gap-2 items-center p-1.5 rounded hover:bg-slate-50 text-xs"><Checkbox checked={newTask.dependencies.includes(t.id)} onCheckedChange={ok=>setNewTask(p=>({...p,dependencies:ok?[...p.dependencies,t.id]:p.dependencies.filter(x=>x!==t.id)}))}/><span>{t.title}</span></label>)}</div></PopoverContent></Popover></div>
                </TabsContent>
              </Tabs>

              <div className="mt-4 -mx-6 px-6 py-3 border-t bg-white flex justify-end gap-2"><Button variant="outline" onClick={()=>setIsCreateDialogOpen(false)}>Annuler</Button><Button className="btn-primary" onClick={handleCreateTask} disabled={!newTask.project_id||!newTask.title} data-testid="submit-task-btn">Créer</Button></div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-3">
        {TASK_STATUSES.map(s => (
          <div key={s.value} className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
            <span className="text-sm font-medium text-slate-700">{s.label}</span>
            <span className="text-sm font-bold text-slate-900">{stats.byStatus[s.value] || 0}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
        <MultiProjectSelect projects={projects} selectedIds={selectedProjectIds} onChange={setSelectedProjectIds} />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {TASK_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={resourceFilter} onValueChange={setResourceFilter}>
          <SelectTrigger className="w-52" data-testid="resource-filter"><SelectValue placeholder="Toutes les ressources" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les ressources</SelectItem>
            {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="text-xs" onClick={async () => {
          const API = process.env.REACT_APP_BACKEND_URL;
          // En mode GANTT -> export Gantt visuel (timeline + legende), supporte 'all' projets
          if (viewMode === 'gantt') {
            const pid = projectFilter !== 'all' ? projectFilter : 'all';
            try {
              const res = await fetch(`${API}/api/tasks/gantt/${pid}/export-excel?group_by=${ganttView}`, { credentials: 'include' });
              if (!res.ok) throw new Error('export failed');
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = `gantt_${pid}_${ganttView}.xlsx`;
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
              toast.success('Gantt Excel telecharge');
              return;
            } catch (err) { console.error('Gantt Excel export error:', err); toast.error('Erreur export Gantt Excel'); return; }
          }
          // Sinon export liste de taches classique
          const params = {};
          if (projectFilter !== 'all') params.project_id = projectFilter;
          if (resourceFilter !== 'all') params.assigned_to = resourceFilter;
          window.open(tasksApi.exportExcelUrl(params), '_blank');
        }} data-testid="export-tasks-excel-btn">
          <Download size={14} className="mr-1 text-green-600" />Excel
        </Button>
        <Button variant="outline" size="sm" className="text-xs" onClick={async () => {
          const API = process.env.REACT_APP_BACKEND_URL;
          // En mode GANTT -> export Gantt visuel A3 PDF, supporte 'all' projets
          if (viewMode === 'gantt') {
            const pid = projectFilter !== 'all' ? projectFilter : 'all';
            try {
              const res = await fetch(`${API}/api/tasks/gantt/${pid}/export-pdf?group_by=${ganttView}`, { credentials: 'include' });
              if (!res.ok) throw new Error('export failed');
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = `gantt_${pid}_${ganttView}.pdf`;
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
              toast.success('Gantt PDF telecharge');
              return;
            } catch (err) { console.error('Gantt PDF export error:', err); toast.error('Erreur export Gantt PDF'); return; }
          }
          // Sinon PDF tableau classique cote client
          const doc = new jsPDF('l', 'mm', 'a4');
          doc.setFontSize(14);
          doc.text('Liste des Taches', 14, 16);
          doc.setFontSize(8);
          doc.text(`Genere le ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 22);
          const rows = filteredByResource.map(t => {
            const proj = projects.find(p => p.id === t.project_id);
            const assignees = getAssignees(t).map(u => `R-${String(u.id||'').slice(-6).toUpperCase()}`).join(', ');
            return [proj?.code || '', t.title, getLabel(t.status, TASK_STATUSES), t.priority || '', assignees, formatDate(t.start_date), formatDate(t.end_date)];
          });
          autoTable(doc, { startY: 26, head: [['Projet', 'Titre', 'Statut', 'Priorite', 'Assignes', 'Debut', 'Fin']], body: rows, styles: { fontSize: 7 }, headStyles: { fillColor: [30, 41, 59] } });
          doc.save('taches.pdf');
          toast.success('PDF genere');
        }} data-testid="export-tasks-pdf-btn">
          <FilePdf size={14} className="mr-1 text-red-500" />PDF
        </Button>
        
        <div className="ml-auto flex items-center gap-2">
          {/* Kanban scope - show only in kanban mode */}
          {viewMode === 'kanban' && (
            <div className="flex items-center gap-1 bg-blue-50 p-1 rounded-lg">
              <Button variant={kanbanScope === 'all' ? 'default' : 'ghost'} size="sm" onClick={() => setKanbanScope('all')} data-testid="kanban-scope-all" className="h-7 text-xs">
                Tout
              </Button>
              <Button variant={kanbanScope === 'my' ? 'default' : 'ghost'} size="sm" onClick={() => setKanbanScope('my')} data-testid="kanban-scope-my" className="h-7 text-xs">
                <User size={14} className="mr-1" />Mes taches
              </Button>
              {isManager && (
                <Button variant={kanbanScope === 'team' ? 'default' : 'ghost'} size="sm" onClick={() => setKanbanScope('team')} data-testid="kanban-scope-team" className="h-7 text-xs">
                  <Users size={14} className="mr-1" />Equipe
                </Button>
              )}
            </div>
          )}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
          <Button variant={viewMode === 'kanban' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('kanban')} data-testid="view-kanban-btn">
            <Kanban size={16} className="mr-1" />Kanban
          </Button>
          <Button variant={viewMode === 'gantt' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('gantt')} data-testid="view-gantt-btn">
            <ChartBar size={16} className="mr-1" />GANTT
          </Button>
          <Button variant={viewMode === 'table' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('table')} data-testid="view-table-btn">
            <TableIcon size={16} className="mr-1" />Tableau
          </Button>
          <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} data-testid="view-list-btn">
            <List size={16} className="mr-1" />Liste
          </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <Card className="card-surface"><CardContent className="py-16"><div className="h-64 bg-slate-200 rounded-lg animate-pulse" /></CardContent></Card>
      ) : tasks.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="py-16 text-center">
            <Target size={56} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Aucune tache</h3>
            <p className="text-slate-500 mb-6">Creez votre premiere tache</p>
            {hasPermission('tasks:create') && <Button className="btn-primary" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus size={18} className="mr-2" />Nouvelle tache
            </Button>}
          </CardContent>
        </Card>
      ) : viewMode === 'kanban' ? (
        /* KANBAN View with Drag & Drop */
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          <div className="flex-shrink-0 w-80 rounded-xl bg-slate-50 border border-slate-200 p-2">
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="font-semibold text-sm text-slate-700">Projet / Tache principale</div>
              {(selectedProjectIds.length > 0 || kanbanRootTaskId) && <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={()=>{setSelectedProjectIds([]);setKanbanRootTaskId(null);}}>Tout afficher</Button>}
            </div>
            <div className="space-y-3">{projects.filter(p=>selectedProjectIds.length===0||selectedProjectIds.includes(p.id)).map(p=>{
              const roots=filteredByResource.filter(t=>t.project_id===p.id&&!t.parent_task_id);
              if(!roots.length)return null;
              const projectActive=selectedProjectIds.length===1&&selectedProjectIds[0]===p.id&&!kanbanRootTaskId;
              return <div key={p.id} className={`bg-white border rounded-lg p-2 ${projectActive?'ring-2 ring-blue-300':''}`}>
                <button type="button" className="w-full text-left text-xs font-bold text-blue-700 hover:text-blue-900" onClick={()=>{setSelectedProjectIds([p.id]);setKanbanRootTaskId(null);}}>
                  {p.code} - {p.name}
                </button>
                {roots.map(t=><button type="button" key={t.id} onClick={()=>{setSelectedProjectIds([p.id]);setKanbanRootTaskId(t.id);}} className={`block w-full text-left mt-1 pl-2 py-1 border-l-2 text-xs rounded-r ${kanbanRootTaskId===t.id?'border-blue-500 bg-blue-50 text-blue-900':'border-slate-200 text-slate-700 hover:bg-slate-50'}`}><span className="text-blue-600 font-semibold">P{t.priority_level ?? 2}.{t.position ?? 1}</span> {t.title}</button>)}
              </div>})}</div>
          </div>
          {TASK_STATUSES.map(status => {
            const columnTasks = kanbanTasks.filter(t => t.status === status.value).sort((a,b)=>(Number(a.priority_level??99)-Number(b.priority_level??99))||(Number(a.position??999)-Number(b.position??999)));
            return (
              <div
                key={status.value}
                className={`flex-shrink-0 w-72 rounded-xl transition-all ${dragOverCol === status.value ? 'bg-blue-50 ring-2 ring-blue-300' : ''}`}
                data-testid={`kanban-col-${status.value}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverCol(status.value); }}
                onDragLeave={() => setDragOverCol(null)}
                onDrop={async (e) => {
                  e.preventDefault();
                  setDragOverCol(null);
                  const taskId = e.dataTransfer.getData('text/plain');
                  if (taskId) {
                    const task = tasks.find(t => t.id === taskId);
                    if (task && task.status !== status.value) {
                      try {
                        await tasksApi.update(taskId, { status: status.value });
                        toast.success(`Tache deplacee vers "${status.label}"`);
                        fetchData();
                      } catch { toast.error('Erreur'); }
                    }
                  }
                }}
              >
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color }} />
                  <span className="text-sm font-semibold text-slate-700">{status.label}</span>
                  <Badge variant="outline" className="ml-auto text-xs">{columnTasks.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[60px]">
                  {columnTasks.map(task => {
                    const assignees = getAssignees(task);
                    const project = projects.find(p => p.id === task.project_id);
                    const isSubtask = !!task.parent_task_id;
                    const subtasks = tasks.filter(t => t.parent_task_id === task.id);
                    return (
                      <Card
                        key={task.id}
                        className={`card-surface cursor-grab hover:shadow-md transition-shadow active:cursor-grabbing ${draggingTaskId === task.id ? 'opacity-40 scale-95' : ''}`}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', task.id);
                          e.dataTransfer.effectAllowed = 'move';
                          setDraggingTaskId(task.id);
                        }}
                        onDragEnd={() => setDraggingTaskId(null)}
                        onClick={() => !draggingTaskId && setSelectedTask(task)}
                        data-testid={`kanban-task-${task.id}`}
                      >
                        <CardContent className="p-3 space-y-2">
                          <div className="rounded-md bg-slate-50 border border-slate-100 px-2 py-1.5 space-y-0.5">
                            {project && <div className="text-[10px] font-semibold text-blue-700">{project.code} - {project.name}</div>}
                            {isSubtask ? <div className="text-[10px] text-slate-600"><span className="font-semibold">Tache :</span> {getParentTask(task)?.title || 'Tache principale non trouvee'}</div> : <div className="text-[10px] text-slate-500">Tache principale</div>}
                          </div>
                          {isSubtask && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><CaretRight size={8} />sous-tache</span>}
                          <div className="flex items-start justify-between gap-1">
                            <p className="font-medium text-sm text-slate-900 line-clamp-2">{task.title}</p>
                            <Badge className={`${getBadgeClass(task.priority, 'priority')} text-[9px] px-1 py-0 flex-shrink-0`}>
                              {`P${task.priority_level ?? 2}.${task.position ?? 1}`}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <CalendarBlank size={11} />{formatDate(task.start_date)} - {formatDate(task.end_date)}
                          </div>
                          {subtasks.length > 0 && (
                            <div className="text-[10px] text-blue-600 font-medium">{subtasks.length} sous-tache{subtasks.length > 1 ? 's' : ''}</div>
                          )}
                          <div className="flex items-center justify-between pt-1">
                            <div className="flex -space-x-1.5">
                              {assignees.slice(0, 3).map(u => (
                                <Avatar key={u.id} className="w-6 h-6 border-2 border-white" title={u.name}>
                                  <AvatarFallback className="text-[8px] bg-blue-100 text-blue-700">{getInitials(u.name || u.email || 'U')}</AvatarFallback>
                                </Avatar>
                              ))}
                              {assignees.length > 3 && <span className="text-[9px] text-slate-400 ml-1">+{assignees.length - 3}</span>}
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-12 h-1 bg-slate-200 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${task.progress || 0}%`, backgroundColor: status.color }} />
                              </div>
                              <span className="text-[9px] text-slate-400">{task.progress || 0}%</span>{task.sprint_id && <Badge className="text-[9px] bg-indigo-50 text-indigo-700">{sprints.find(sp=>sp.id===task.sprint_id)?.name || 'Sprint'}</Badge>}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {columnTasks.length === 0 && (
                    <div className={`text-center py-8 text-xs text-slate-400 border-2 border-dashed rounded-lg transition-colors ${dragOverCol === status.value ? 'border-blue-400 bg-blue-50 text-blue-500' : 'border-slate-200'}`}>
                      {dragOverCol === status.value ? 'Deposer ici' : 'Aucune tache'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : viewMode === 'gantt' ? (
        /* GANTT View with Drag & Drop */
        <Card className="card-surface">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <ChartBar size={20} className="text-blue-600" />
                Diagramme de GANTT
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
                  <Button variant={ganttView === 'task' ? 'default' : 'ghost'} size="sm" onClick={() => setGanttView('task')} className="h-7 text-xs" data-testid="gantt-view-task">Par Tache</Button>
                  <Button variant={ganttView === 'project' ? 'default' : 'ghost'} size="sm" onClick={() => setGanttView('project')} className="h-7 text-xs" data-testid="gantt-view-project">Par Projet</Button>
                  <Button variant={ganttView === 'resource' ? 'default' : 'ghost'} size="sm" onClick={() => setGanttView('resource')} className="h-7 text-xs" data-testid="gantt-view-resource">Par Ressource</Button>
                </div>
                {projectFilter !== 'all' && (
                  <>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={async () => {
                      try {
                        const API = process.env.REACT_APP_BACKEND_URL;
                        const res = await fetch(`${API}/api/tasks/gantt/${projectFilter}/export-pdf`, { credentials: 'include' });
                        if (!res.ok) throw new Error();
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url; a.download = 'gantt.pdf'; a.click();
                        window.URL.revokeObjectURL(url); toast.success('Gantt PDF telecharge');
                      } catch { toast.error('Erreur export PDF'); }
                    }} data-testid="gantt-export-pdf"><Download size={12} className="mr-1" />PDF A3</Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={async () => {
                      try {
                        const API = process.env.REACT_APP_BACKEND_URL;
                        const res = await fetch(`${API}/api/tasks/gantt/${projectFilter}/export-excel`, { credentials: 'include' });
                        if (!res.ok) throw new Error();
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url; a.download = 'gantt.xlsx'; a.click();
                        window.URL.revokeObjectURL(url); toast.success('Gantt Excel telecharge');
                      } catch { toast.error('Erreur export Excel'); }
                    }} data-testid="gantt-export-excel"><Download size={12} className="mr-1" />Excel</Button>
                  </>
                )}
              </div>
            </div>
            <span className="text-xs text-slate-400">Glissez les barres pour modifier les dates | Cliquez pour modifier</span>
          </CardHeader>
          <CardContent className="p-0">
            <div className="gantt-container overflow-x-auto">
              <div className="min-w-[1200px]">
                <div className="flex border-b border-slate-200 bg-slate-50">
                  <div className="w-72 flex-shrink-0 px-4 py-3 font-semibold text-slate-700 border-r border-slate-200 text-sm">
                    {ganttView === 'resource' ? 'Ressource / Tache' : ganttView === 'project' ? 'Projet / Tache' : 'Tache'}
                  </div>
                  <div className="flex-1 flex">
                    {ganttData.days.filter((_, i) => i % 3 === 0).map((day, i) => (
                      <div key={i} className="flex-1 text-center py-2 text-xs text-slate-500 border-r border-slate-100">
                        {format(day, 'dd/MM', { locale: fr })}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Pave Absences supprime du haut, deplace en bas */}

                {ganttView === 'resource' ? (
                  /* Group by Resource */
                  (() => {
                    const byUser = {};
                    filteredByResource.forEach(t => {
                      const ids = Array.isArray(t.assigned_to) ? t.assigned_to : (t.assigned_to ? [t.assigned_to] : []);
                      if (ids.length === 0) {
                        byUser['unassigned'] = byUser['unassigned'] || { name: 'Non assigne', tasks: [] };
                        byUser['unassigned'].tasks.push(t);
                      }
                      ids.forEach(uid => {
                        const u = users.find(x => x.id === uid);
                        byUser[uid] = byUser[uid] || { name: u?.name || uid, tasks: [] };
                        byUser[uid].tasks.push(t);
                      });
                    });
                    return Object.entries(byUser).map(([uid, data]) => {
                      // Find absences for this user
                      const userAbsences = absences.filter(a => a.user_id === uid);
                      return (
                        <div key={uid}>
                          <div className="flex bg-blue-50/60 border-b border-blue-200">
                            <div className="w-72 flex-shrink-0 px-4 py-2 border-r border-blue-200">
                              <span className="font-semibold text-sm text-blue-800"><Users size={14} className="inline mr-1" />{data.name}</span>
                              <span className="text-xs text-blue-500 ml-2">({data.tasks.length})</span>
                            </div>
                            <div className="flex-1 relative py-1">
                              {userAbsences.map(a => {
                                const aPos = getTaskPosition({ start_date: a.start_date, end_date: a.end_date });
                                const reasonLabels = { cp:'CP', conge:'CP', rtt:'RTT', repos:'Repos', recuperation:'Recup.', formation:'Formation', maladie:'Maladie', autre:'Autre' };
                                const reasonColors = { cp:'#f59e0b', conge:'#f59e0b', rtt:'#8b5cf6', repos:'#8b5cf6', recuperation:'#6366f1', formation:'#0ea5e9', maladie:'#ef4444', autre:'#6b7280' };
                                return (
                                  <div key={a.id} className="absolute top-0 bottom-0 opacity-30 rounded"
                                    style={{ left: aPos.left, width: aPos.width, background: reasonColors[a.reason] || '#6b7280' }}
                                    title={`${reasonLabels[a.reason] || a.reason} - ${a.description || ''}`}
                                  />
                                );
                              })}
                            </div>
                          </div>
                          {hierarchyOrder(data.tasks).map(task => renderGanttRow(task, true))}
                        </div>
                      );
                    });
                  })()
                ) : ganttView === 'project' ? (
                  /* Group by Project */
                  (() => {
                    const byProject = {};
                    filteredByResource.forEach(t => {
                      const p = projects.find(x => x.id === t.project_id);
                      const pName = p ? `${p.code} - ${p.name}` : t.project_id;
                      byProject[t.project_id] = byProject[t.project_id] || { name: pName, tasks: [] };
                      byProject[t.project_id].tasks.push(t);
                    });
                    return Object.entries(byProject).map(([pid, data]) => (
                      <div key={pid}>
                        <div className="flex bg-violet-50/60 border-b border-violet-200">
                          <div className="w-72 flex-shrink-0 px-4 py-2 border-r border-violet-200">
                            <span className="font-semibold text-sm text-violet-800"><Target size={14} className="inline mr-1" />{data.name}</span>
                            <span className="text-xs text-violet-500 ml-2">({data.tasks.length})</span>
                          </div>
                          <div className="flex-1"></div>
                        </div>
                        {hierarchyOrder(data.tasks).map(task => renderGanttRow(task, true))}
                      </div>
                    ));
                  })()
                ) : (
                  /* Default: by task */
                  ganttTasksOrdered.map(task => renderGanttRow(task, false))
                )}
                
                {/* Sprints */}
                {sprints.filter(sp => selectedProjectIds.length === 0 || selectedProjectIds.includes(sp.project_id)).length > 0 && (
                  <div className="border-t-2 border-indigo-200 bg-indigo-50/40">
                    <div className="flex border-b border-indigo-100"><div className="w-72 flex-shrink-0 px-4 py-2 border-r border-indigo-100"><span className="font-medium text-sm text-indigo-700">Sprints</span></div><div className="flex-1 relative min-h-[34px]">{sprints.filter(sp=>selectedProjectIds.length===0||selectedProjectIds.includes(sp.project_id)).map(sp=>{ const pos=getTaskPosition({start_date:sp.start_date,end_date:sp.end_date}); return <div key={sp.id} className="absolute top-1 h-6 rounded bg-indigo-500/80 text-white px-2 text-[10px] flex items-center overflow-hidden" style={{left:pos.left,width:pos.width}} title={`${sp.name} ${formatDate(sp.start_date)} - ${formatDate(sp.end_date)}`}>{sp.name}</div>;})}</div></div>
                  </div>
                )}

                {/* Milestones */}
                {milestones.length > 0 && (
                  <div className="flex border-t-2 border-amber-200 bg-amber-50/50">
                    <div className="w-72 flex-shrink-0 px-4 py-3 border-r border-slate-200">
                      <div className="flex items-center gap-2">
                        <Flag size={16} className="text-amber-600" />
                        <span className="font-medium text-sm text-amber-700">Jalons</span>
                      </div>
                    </div>
                    <div className="flex-1 relative py-2">
                      {milestones.map(m => {
                        const mpos = getMilestonePosition(m);
                        return (
                          <div key={m.id} className="gantt-milestone" style={{ left: mpos.left }} title={`${m.name} - ${formatDate(m.target_date)}`} />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Absences pave - en bas du Gantt */}
                {(() => {
                  const visibleUserIds = new Set();
                  filteredByResource.forEach(t => {
                    (Array.isArray(t.assigned_to) ? t.assigned_to : (t.assigned_to ? [t.assigned_to] : [])).forEach(uid => visibleUserIds.add(uid));
                  });
                  const visibleAbsences = absences.filter(a => visibleUserIds.has(a.user_id));
                  if (visibleAbsences.length === 0) return null;
                  const reasonLabels = { cp:'CP', conge:'CP', rtt:'RTT', repos:'Repos', recuperation:'Recup.', formation:'Formation', maladie:'Maladie', autre:'Autre' };
                  const reasonColors = { cp:'#f59e0b', conge:'#f59e0b', rtt:'#8b5cf6', repos:'#8b5cf6', recuperation:'#6366f1', formation:'#0ea5e9', maladie:'#ef4444', autre:'#6b7280' };
                  return (
                    <div className="border-t-2 border-rose-300" data-testid="gantt-absences-pave">
                      <div className="flex bg-rose-100 border-b border-rose-200">
                        <div className="w-72 flex-shrink-0 px-4 py-2 border-r border-rose-200">
                          <span className="font-semibold text-sm text-rose-800">
                            <CalendarOff size={14} className="inline mr-1" />Absences planifiees
                          </span>
                          <span className="text-xs text-rose-600 ml-2">({visibleAbsences.length})</span>
                        </div>
                        <div className="flex-1 flex items-center gap-3 px-3">
                          {Object.entries(reasonLabels).map(([k, lbl]) => (
                            <span key={k} className="inline-flex items-center gap-1 text-xs text-rose-700">
                              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: reasonColors[k] }} />
                              {lbl}
                            </span>
                          ))}
                        </div>
                      </div>
                      {visibleAbsences.map(a => {
                        const u = users.find(x => x.id === a.user_id);
                        const aPos = getTaskPosition({ start_date: a.start_date, end_date: a.end_date });
                        const sd=safeJsDate(a.start_date), ed=safeJsDate(a.end_date); const days=(sd&&ed)?Math.max(1,Math.ceil((ed-sd)/86400000)+1):1;
                        return (
                          <div key={a.id} className="flex border-b border-rose-100 hover:bg-rose-50/40">
                            <div className="w-72 flex-shrink-0 px-4 py-1.5 border-r border-rose-100 text-xs">
                              <span className="font-medium text-slate-700">{u?.name || a.user_name || 'Utilisateur inconnu'}</span>
                              <span className="ml-2 text-rose-600">[{reasonLabels[a.reason] || a.reason}]</span>
                              <span className="ml-2 text-slate-400">{days}j</span>
                            </div>
                            <div className="flex-1 relative" style={{ height: 22 }}>
                              <div
                                className="absolute top-1 bottom-1 rounded shadow-sm flex items-center justify-center text-[10px] text-white font-semibold"
                                style={{ left: aPos.left, width: aPos.width, background: reasonColors[a.reason] || reasonColors.autre, opacity: 0.85 }}
                                title={`${reasonLabels[a.reason] || a.reason} - ${a.description || ''} (${days}j)`}
                              >
                                {aPos.width && parseFloat(aPos.width) > 60 ? (a.description || reasonLabels[a.reason]) : ''}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        /* Table View */
        <Card className="card-surface">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold w-8">#</TableHead>
                  <TableHead className="font-semibold">Titre</TableHead>
                  <TableHead className="font-semibold">Projet</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                  <TableHead className="font-semibold">Priorite</TableHead>
                  <TableHead className="font-semibold">Assigne(s)</TableHead>
                  <TableHead className="font-semibold">Debut</TableHead>
                  <TableHead className="font-semibold">Fin</TableHead>
                  <TableHead className="font-semibold">Progres</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task, idx) => {
                  const assignees = getAssignees(task);
                  const project = projects.find(p => p.id === task.project_id);
                  return (
                    <TableRow key={task.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedTask(task)} data-testid={`table-task-${task.id}`}>
                      <TableCell className="text-slate-400 text-xs">{idx + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {!!task.parent_task_id && <span className="text-slate-300 text-xs">&#8627;</span>}
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getTaskStatusColor(task.status) }} />
                          <span className="font-medium text-sm">{task.title}</span>
                          {tasks.filter(t => t.parent_task_id === task.id).length > 0 && (
                            <span className="text-[9px] text-blue-500 bg-blue-50 px-1 rounded">{tasks.filter(t => t.parent_task_id === task.id).length} sub</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono text-slate-600">{project?.code || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <Select value={task.status} onValueChange={(v) => { v !== task.status && handleUpdateTaskStatus(task.id, v); }}>
                          <SelectTrigger className="w-32 h-7 text-xs" onClick={(e) => e.stopPropagation()}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {allowedTaskStatuses(task,TASK_STATUSES).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getBadgeClass(task.priority, 'priority')} text-xs`}>
                          {`P${task.priority_level ?? 2}.${task.position ?? 1}`}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex -space-x-1.5">
                          {assignees.slice(0, 3).map(u => (
                            <Avatar key={u.id} className="w-6 h-6 border-2 border-white" title={u.name}>
                              <AvatarFallback className="text-[8px] bg-blue-100 text-blue-700">{getInitials(u.name || u.email || 'U')}</AvatarFallback>
                            </Avatar>
                          ))}
                          {assignees.length > 3 && <span className="text-xs text-slate-500 ml-1">+{assignees.length - 3}</span>}
                          {assignees.length === 0 && <span className="text-xs text-slate-400">-</span>}
                          {task.self_assignment_enabled && user?.id && !(task.assigned_to || []).includes(user.id) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-6 ml-2 px-2 text-[10px] border-blue-200 text-blue-700"
                              onClick={(e) => { e.stopPropagation(); handleSelfAssign(task.id); }}
                            >
                              Me positionner
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{formatDate(task.start_date)}</TableCell>
                      <TableCell className="text-xs text-slate-600">{formatDate(task.end_date)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${task.progress || 0}%`, backgroundColor: getTaskStatusColor(task.status) }} />
                          </div>
                          <span className="text-xs text-slate-500">{task.progress || 0}%</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        /* List/Kanban View */
        <Card className="card-surface">
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {tasks.map(task => {
                const assignees = getAssignees(task);
                const project = projects.find(p => p.id === task.project_id);
                
                return (
                  <div key={task.id} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors" data-testid={`list-task-${task.id}`}>
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: getTaskStatusColor(task.status) }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-slate-500">{project?.code}</span>
                        <p className="font-medium text-slate-900 truncate">{task.title}</p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <CalendarBlank size={12} />
                          {formatDate(task.start_date)} - {formatDate(task.end_date)}
                        </span>
                        {Number(task.estimated_days || 0) > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {Number(task.estimated_days || 0).toFixed(2)} J · {Number(task.scheduled_days || 0).toFixed(2)} J positionnés
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex -space-x-1.5">
                      {assignees.slice(0, 3).map(u => (
                        <Avatar key={u.id} className="w-7 h-7 border-2 border-white" title={u.name}>
                          <AvatarFallback className="text-[9px] bg-blue-100 text-blue-700">{getInitials(u.name || u.email || 'U')}</AvatarFallback>
                        </Avatar>
                      ))}
                      {assignees.length > 3 && <span className="text-xs text-slate-500 ml-1">+{assignees.length - 3}</span>}
                    </div>
                    <Badge className={getBadgeClass(task.priority, 'priority')}>
                      {`P${task.priority_level ?? 2}.${task.position ?? 1}`}
                    </Badge>
                    <Select value={task.status} onValueChange={(v) => handleUpdateTaskStatus(task.id, v)}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {allowedTaskStatuses(task,TASK_STATUSES).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Task Edit Dialog - Full Form */}
      <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
        <DialogContent className="dialog-wide max-w-6xl h-[88vh] max-h-[88vh] overflow-hidden flex flex-col" data-testid="edit-task-dialog">
          <DialogHeader>
            <DialogTitle>Modifier la tache</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <EditTaskForm
              task={selectedTask}
              taskStatuses={TASK_STATUSES}
              projects={projects}
              users={users}
              allTasks={tasks}
              sprints={sprints}
              onSave={async (updatedData, planning) => {
                try {
                  const oldAssigned=new Set(Array.isArray(selectedTask.assigned_to)?selectedTask.assigned_to:(selectedTask.assigned_to?[selectedTask.assigned_to]:[]));
                  const newAssigned=new Set(updatedData.assigned_to||[]);
                  const removed=[...oldAssigned].filter(x=>!newAssigned.has(x));
                  const oldStart=String(selectedTask.start_date||'').slice(0,10); const oldEnd=String(selectedTask.end_date||'').slice(0,10);
                  const newStart=String(updatedData.start_date||'').slice(0,10); const newEnd=String(updatedData.end_date||'').slice(0,10);
                  const shrinking=!!oldStart&&!!oldEnd&&((newStart&&newStart>oldStart)||(newEnd&&newEnd<oldEnd));
                  if (planning && (removed.length||shrinking)) {
                    const transition={...planning,allocations:(planning.allocations||[]).filter(a=>oldAssigned.has(a.user_id)).map(a=>({...a,days:(a.days||[]).filter(d=>(!oldStart||d.date>=oldStart)&&(!oldEnd||d.date<=oldEnd))}))};
                    await taskPlanningApi.save(selectedTask.id,transition);
                  }
                  await tasksApi.update(selectedTask.id, updatedData);
                  if (planning) await taskPlanningApi.save(selectedTask.id,planning);
                  toast.success('Tache et charge mises a jour');
                  setSelectedTask(null);
                  fetchData();
                } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
              }}
              onDelete={hasPermission('tasks:delete') ? async () => {
                try {
                  await tasksApi.delete(selectedTask.id);
                  toast.success('Tache supprimee');
                  setSelectedTask(null);
                  fetchData();
                } catch { toast.error('Erreur'); }
              } : null}
              onCancel={() => setSelectedTask(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="import-csv-dialog">
          <DialogHeader>
            <DialogTitle>Importer des taches</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Projet</Label>
              <Select value={importProjectId} onValueChange={setImportProjectId}>
                <SelectTrigger data-testid="import-project-select"><SelectValue placeholder="Projet indique dans le fichier" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="from-file">Selon la colonne Projet du fichier (multi-projets)</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">Laissez le mode multi-projets pour creer ou mettre a jour des taches sur plusieurs projets avec un seul Excel.</p>
            </div>
            <div className="space-y-2">
              <Label>Fichier Excel (.xlsx) ou CSV *</Label>
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="import-csv-file-input" />
            </div>
            <div className="bg-slate-50 p-3 rounded-lg text-xs text-slate-600 space-y-1">
              <p className="font-semibold">Import V4 creation / mise a jour :</p>
              <p>Code tache | Titre | Description | Projet | Date debut | Date fin | Statut | Priorite | Assigne(s) | Charge (J) | Tache parente | Jalon | Sprint</p>
              <p className="text-slate-500">- <b>Code tache vide</b> : creation avec numerotation automatique.</p>
              <p className="text-slate-500">- <b>Code tache renseigne</b> : mise a jour de la tache existante.</p>
              <p className="text-slate-500">- Les cellules vides ne remplacent pas les valeurs existantes lors d'une mise a jour.</p>
              <button onClick={handleDownloadTaskTemplate} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium mt-1" data-testid="download-csv-template-link">
                <DownloadSimple size={12} />Telecharger le modele Excel
              </button>
            </div>
            {importResult && (
              <div className={`p-3 rounded-lg text-sm ${importResult.errors?.length > 0 ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`}>
                <p className="font-semibold">{importResult.dry_run ? 'Controle' : 'Traitement'} : {importResult.created || 0} creation(s), {importResult.updated || 0} mise(s) a jour, {importResult.unchanged || 0} inchangee(s)</p>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-xs max-h-32 overflow-y-auto">
                    {importResult.errors.map((err, i) => <li key={i}>- {err}</li>)}
                  </ul>
                )}
                <Button type="button" variant="outline" size="sm" className="mt-2 bg-white" onClick={downloadImportReport}>
                  <DownloadSimple size={12} className="mr-1"/>Telecharger le rapport
                </Button>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => { setIsImportDialogOpen(false); setImportResult(null); }}>Fermer</Button>
              <Button variant="outline" onClick={() => handleImportCSV(true)} disabled={!importFile || importingCSV}>
                {importingCSV ? 'Controle...' : 'Controler le fichier'}
              </Button>
              <Button className="btn-primary" onClick={() => handleImportCSV(false)} disabled={!importFile || importingCSV} data-testid="submit-import-btn">
                {importingCSV ? 'Traitement...' : 'Creer / Mettre a jour'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function TaskChargePlanner({ task, assignedIds = [], plannedDays = 0, startDate, endDate, workingWeekdays, onPlanChange }) {
  return <TaskResourcePlanningPanel task={task} assignedIds={assignedIds} plannedDays={plannedDays} startDate={startDate} endDate={endDate} workingWeekdays={workingWeekdays} onPlanChange={onPlanChange} />;
}

function EditTaskForm({ task, projects, users, allTasks, sprints = [], taskStatuses = FALLBACK_TASK_STATUSES, onSave, onDelete, onCancel }) {
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [planningPayload, setPlanningPayload] = useState(null);
  const [form, setForm] = useState({
    project_id: task.project_id || '',
    title: task.title || '',
    description: task.description || '',
    start_date: safeJsDate(task.start_date) || new Date(),
    end_date: safeJsDate(task.end_date) || addDays(new Date(), 7),
    estimated_days: task.estimated_days != null ? String(task.estimated_days) : '',
    working_weekdays: Array.isArray(task.working_weekdays) && task.working_weekdays.length ? task.working_weekdays : [0,1,2,3,4],
    status: task.status || 'todo', priority: task.priority || 'medium', priority_level: task.priority_level ?? 2,
    position: task.position ?? 1,
    assigned_to: Array.isArray(task.assigned_to) ? task.assigned_to : (task.assigned_to ? [task.assigned_to] : []),
    parent_task_id: task.parent_task_id || '', sprint_id: task.sprint_id || '',
    dependencies: Array.isArray(task.dependencies) ? task.dependencies : [], progress: task.progress != null ? task.progress : 0,
    client_visible: !!task.client_visible, self_assignment_enabled: !!task.self_assignment_enabled,
  });
  const parentTasks = allTasks.filter(t => !t.parent_task_id && t.id !== task.id);
  const subtasks = allTasks.filter(t => t.parent_task_id === task.id);

  const loadDependencyImpact = async () => {
    setImpactLoading(true);
    try { const r = await tasksApi.dependencyImpact(task.id); setImpact(r.data || { impacted_tasks: [] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Impossible d'analyser l'impact des dependances"); }
    finally { setImpactLoading(false); }
  };
  const handleSubmit = () => onSave({
    project_id: form.project_id, title: form.title, description: form.description,
    start_date: form.start_date.toISOString(), end_date: form.end_date.toISOString(),
    estimated_days: form.estimated_days !== '' ? parseFloat(form.estimated_days) : null,
    working_weekdays: form.working_weekdays || [0,1,2,3,4], status: form.status, priority: form.priority,
    priority_level: parseInt(form.priority_level ?? 2), position: parseInt(form.position ?? 1), assigned_to: form.assigned_to,
    parent_task_id: form.parent_task_id || null, sprint_id: form.sprint_id || null,
    dependencies: form.dependencies || [], progress: form.progress, client_visible: !!form.client_visible,
    self_assignment_enabled: !!form.self_assignment_enabled,
  }, planningPayload);

  return <div className="flex-1 min-h-0 flex flex-col">
    <div className="grid lg:grid-cols-[1.35fr_.55fr_.45fr_.55fr] gap-3 mb-4">
      <div><Label className="text-xs">Titre *</Label><Input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} className="h-10 font-medium" data-testid="edit-task-title-input"/></div>
      <div><Label className="text-xs">Statut</Label><Select value={form.status} onValueChange={v=>setForm(p=>({...p,status:v}))}><SelectTrigger className="h-10"><SelectValue/></SelectTrigger><SelectContent>{taskStatuses.map(x=><SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent></Select></div>
      <div><Label className="text-xs">Priorité</Label><Input type="number" min="0" step="1" value={form.priority_level} onChange={e=>setForm(p=>({...p,priority_level:e.target.value}))} className="h-10"/></div>
      <div><Label className="text-xs">Progression</Label><div className="h-10 rounded-lg border bg-slate-50 flex items-center gap-2 px-3"><div className="h-2 flex-1 rounded bg-slate-200 overflow-hidden"><div className="h-full bg-emerald-500" style={{width:`${Math.max(0,Math.min(100,form.progress))}%`}}/></div><b className="text-xs">{form.progress}%</b></div></div>
    </div>

    <Tabs defaultValue="general" className="flex-1 min-h-0 flex flex-col">
      <TabsList className="w-full justify-start bg-transparent border-b rounded-none h-auto p-0">
        <TabsTrigger value="general" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Général</TabsTrigger>
        <TabsTrigger value="delay" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Délais & charge</TabsTrigger>
        <TabsTrigger value="resources" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Ressources</TabsTrigger>
        <TabsTrigger value="deps" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Dépendances</TabsTrigger>
      </TabsList>

      <TabsContent value="general" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
        <div className="grid md:grid-cols-[.75fr_1.25fr] gap-3">
          <div className="rounded-xl border bg-white p-4 space-y-3"><div><Label>Projet</Label><Select value={form.project_id} onValueChange={v=>setForm(p=>({...p,project_id:v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{projects.map(pr=><SelectItem key={pr.id} value={pr.id}>{pr.code} - {pr.name}</SelectItem>)}</SelectContent></Select></div><div><Label>Position</Label><Input type="number" min="1" step="1" value={form.position} onChange={e=>setForm(p=>({...p,position:e.target.value}))}/></div></div>
          <div className="rounded-xl border bg-white p-4"><Label>Description</Label><Textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={5} placeholder="Description de la tâche..."/></div>
        </div>
        <div className="grid md:grid-cols-2 gap-3"><div className="rounded-xl border bg-slate-50 p-3 flex items-center justify-between gap-3"><div><b className="text-sm">Visible par le client</b><p className="text-[11px] text-slate-500">Afficher cette tâche dans le portail client interne.</p></div><Checkbox checked={form.client_visible} onCheckedChange={v=>setForm(p=>({...p,client_visible:!!v}))}/></div><div className="rounded-xl border bg-blue-50/60 p-3 flex items-center justify-between gap-3"><div><b className="text-sm">Auto-affectation</b><p className="text-[11px] text-slate-500">Autoriser un membre de l'équipe à se positionner lui-même.</p></div><Checkbox checked={form.self_assignment_enabled} onCheckedChange={v=>setForm(p=>({...p,self_assignment_enabled:!!v}))}/></div></div>
      </TabsContent>

      <TabsContent value="delay" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
        <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3 text-xs text-blue-800"><b>Délais et charge sont séparés.</b> Les dates définissent uniquement la fenêtre de réalisation. La charge reste en J/JH et sera répartie sur les ressources dans l'onglet Ressources.</div>
        <div className="grid lg:grid-cols-[1.2fr_.65fr] gap-3">
          <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-4"><CalendarBlank size={19} className="text-blue-600"/><div><b>Fenêtre de réalisation</b><p className="text-[11px] text-slate-500">Période autorisée pour réaliser la tâche.</p></div></div><div className="grid grid-cols-2 gap-3"><div><Label>Date début</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarBlank size={15} className="mr-2"/>{format(form.start_date,'dd/MM/yyyy',{locale:fr})}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={form.start_date} onSelect={d=>d&&setForm(p=>({...p,start_date:d}))} locale={fr}/></PopoverContent></Popover></div><div><Label>Date fin</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarBlank size={15} className="mr-2"/>{format(form.end_date,'dd/MM/yyyy',{locale:fr})}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={form.end_date} onSelect={d=>d&&setForm(p=>({...p,end_date:d}))} locale={fr}/></PopoverContent></Popover></div></div></div>
          <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-4"><Clock size={19} className="text-indigo-600"/><div><b>Charge de la tâche</b><p className="text-[11px] text-slate-500">Unité métier : J/JH.</p></div></div><Label>Charge prévue (J)</Label><Input type="number" min="0" step="0.01" value={form.estimated_days} onChange={e=>setForm(p=>({...p,estimated_days:e.target.value}))} placeholder="Ex : 5,00" className="text-lg font-semibold"/></div>
        </div>
        <div className="rounded-xl border bg-white p-4"><Label>Jours travaillés autorisés pour le lissage</Label><div className="mt-2"><WorkingDaysSelector value={form.working_weekdays} onChange={v=>setForm(p=>({...p,working_weekdays:v}))}/></div></div>
      </TabsContent>

      <TabsContent value="resources" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
        <div className="rounded-xl border bg-white p-4"><div className="flex items-center justify-between gap-3 mb-2"><div><b>Affectation des ressources</b><p className="text-[11px] text-slate-500">Une même tâche peut être partagée entre plusieurs ressources avec des charges et des jours différents.</p></div>{form.self_assignment_enabled&&<Badge className="bg-blue-100 text-blue-700">Auto-affectation autorisée</Badge>}</div><MultiUserSelect users={users} selectedIds={form.assigned_to} onChange={ids=>setForm(p=>({...p,assigned_to:ids}))} placeholder="Affecter une ou plusieurs ressources..."/></div>
        <TaskChargePlanner task={task} assignedIds={form.assigned_to} plannedDays={form.estimated_days} startDate={form.start_date} endDate={form.end_date} workingWeekdays={form.working_weekdays} onPlanChange={setPlanningPayload}/>
      </TabsContent>

      <TabsContent value="deps" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
        <div className="grid md:grid-cols-2 gap-3"><div className="rounded-xl border bg-white p-4"><Label>Sous-tâche de</Label><Select value={form.parent_task_id||'none'} onValueChange={v=>setForm(p=>({...p,parent_task_id:v==='none'?'':v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="none">Aucune (tâche principale)</SelectItem>{parentTasks.map(t=><SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent></Select></div><div className="rounded-xl border bg-white p-4"><Label>Sprint</Label><Select value={form.sprint_id||'none'} onValueChange={v=>setForm(p=>({...p,sprint_id:v==='none'?'':v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="none">Aucun sprint</SelectItem>{sprints.filter(sp=>sp.project_id===form.project_id).map(sp=><SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}</SelectContent></Select></div></div>
        <div className="rounded-xl border bg-white p-4"><Label>Dépend de</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start font-normal">{form.dependencies.length?`${form.dependencies.length} dépendance(s)`:'Aucune dépendance'}</Button></PopoverTrigger><PopoverContent className="w-80 p-2"><div className="max-h-56 overflow-y-auto">{allTasks.filter(t=>t.project_id===form.project_id&&t.id!==task.id).map(t=><label key={t.id} className="flex gap-2 p-1.5"><Checkbox checked={form.dependencies.includes(t.id)} onCheckedChange={v=>setForm(p=>({...p,dependencies:v?[...p.dependencies,t.id]:p.dependencies.filter(x=>x!==t.id)}))}/><span className="text-xs">P{t.priority_level??2}.{t.position??1} - {t.title}</span></label>)}</div></PopoverContent></Popover></div>
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-3"><div className="flex items-center justify-between gap-3"><div><b className="text-sm">Impact des dépendances</b><p className="text-[11px] text-slate-500">Identifier les tâches impactées par un décalage de celle-ci.</p></div><Button type="button" variant="outline" size="sm" onClick={loadDependencyImpact} disabled={impactLoading}>{impactLoading?'Analyse...':'Voir impact'}</Button></div>{impact&&<div className="mt-2 text-xs text-indigo-800"><b>{impact.impacted_tasks?.length||0}</b> tâche(s) impactée(s){(impact.impacted_tasks||[]).slice(0,8).map(t=><div key={t.id} className="mt-1">- {t.title}</div>)}</div>}</div>
        {subtasks.length>0&&<div className="rounded-xl border bg-blue-50 p-3 text-xs text-blue-700"><b>{subtasks.length} sous-tâche(s)</b>{subtasks.map(st=><div key={st.id} className="mt-1">- {st.title}</div>)}</div>}
      </TabsContent>
    </Tabs>

    <div className="mt-4 -mx-6 px-6 py-3 border-t bg-white flex items-center justify-between gap-3">
      {onDelete?<Button variant="ghost" className="text-red-600" onClick={onDelete}><Trash size={16} className="mr-1"/>Supprimer</Button>:<div/>}
      <div className="flex gap-2"><Button type="button" variant="outline" onClick={()=>{const q=new URLSearchParams({new:'1',project_id:form.project_id||task.project_id||'',task_id:task.id,title:`Intervention - ${form.title||task.title||''}`});window.location.href=`/interventions?${q.toString()}`;}}><Wrench size={16} className="mr-1"/>Créer intervention</Button><Button variant="outline" onClick={onCancel}>Annuler</Button><Button className="btn-primary" onClick={handleSubmit} disabled={!form.project_id||!form.title}>Enregistrer</Button></div>
    </div>
  </div>;
}
